import type { HistoryPoint } from './report';
import { REPORT } from './tokens';

const WIDTH = 720;
const HEIGHT = 176;
const PLOT_LEFT = 28;
const PLOT_RIGHT = 720;
const FIRST_X = 36;
const LAST_X = 684;
const TOP_Y = 10;
const BASELINE = 138;
const AXIS_X = 16;
const LABEL_Y = 164;
const MAX_LABELS = 8;

export const SPEAKING_STROKE = '#08081B';
export const WRITING_STROKE = '#7F7F99';

export function HistoryChart({ history, top }: { history: HistoryPoint[]; top: number }) {
  const y = (value: number) => BASELINE - ((value - 1) / Math.max(top - 1, 1)) * (BASELINE - TOP_Y);
  const x = (i: number) => (history.length === 1 ? (FIRST_X + LAST_X) / 2 : FIRST_X + (i / (history.length - 1)) * (LAST_X - FIRST_X));

  const segments = (pick: (p: HistoryPoint) => number | null): string[] => {
    const runs: string[][] = [];
    let run: string[] = [];
    history.forEach((p, i) => {
      const v = pick(p);
      if (v == null) {
        if (run.length > 0) runs.push(run);
        run = [];
        return;
      }
      run.push(`${x(i)},${y(v)}`);
    });
    if (run.length > 0) runs.push(run);
    return runs.map((points) => points.join(' '));
  };

  const speaking = segments((p) => p.speaking);
  const writing = segments((p) => p.writing);
  const last = history.length - 1;
  const labelStep = Math.max(1, Math.ceil(last / (MAX_LABELS - 1)));
  const labelled = (i: number) => (last - i) % labelStep === 0;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={WIDTH}
      height={HEIGHT}
      role="img"
      aria-label="Benchmark growth history"
      style={{ display: 'block', fontFamily: REPORT.font, fontSize: 12 }}
    >
      {Array.from({ length: top }, (_, i) => i + 1).map((tick) => (
        <g key={`tick-${tick}`}>
          {tick > 1 && <line x1={PLOT_LEFT} x2={PLOT_RIGHT} y1={y(tick)} y2={y(tick)} stroke={REPORT.grid} />}
          <text x={AXIS_X} y={y(tick) + 4} textAnchor="end" fill={REPORT.muted} fontWeight={700}>
            {tick}
          </text>
        </g>
      ))}
      <line x1={PLOT_LEFT} x2={PLOT_RIGHT} y1={BASELINE} y2={BASELINE} stroke={REPORT.ink} strokeWidth={2} />
      {writing.map((points) => (
        <polyline
          key={points}
          data-testid="history-writing"
          points={points}
          fill="none"
          stroke={WRITING_STROKE}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="7 5"
        />
      ))}
      {speaking.map((points) => (
        <polyline
          key={points}
          data-testid="history-speaking"
          points={points}
          fill="none"
          stroke={SPEAKING_STROKE}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {history.map((p, i) => {
        const square = i === last ? 10 : 8;
        return (
          <g key={p.date}>
            {p.writing != null && (
              <rect
                data-testid="history-writing-point"
                x={x(i) - square / 2}
                y={y(p.writing) - square / 2}
                width={square}
                height={square}
                fill={WRITING_STROKE}
              />
            )}
            {p.speaking != null && <circle cx={x(i)} cy={y(p.speaking)} r={i === last ? 6.5 : 4} fill={SPEAKING_STROKE} />}
            {labelled(i) && (
              <text data-testid="history-label" x={x(i)} y={LABEL_Y} textAnchor="middle" fill={REPORT.ink} fontWeight={600}>
                {p.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
